import React, { useState, useEffect } from 'react';
import { Target, Calendar, CheckCircle, ClipboardList, BarChart, GraduationCap, BookOpen, Briefcase, Trophy, TrendingUp, ArrowRight, Globe, MapPin, Plane, Building, Users, Award, FileText, Lightbulb, Rocket, Star, Book } from 'lucide-react';
import { Link } from 'react-router-dom';
import * as api from '../../../api/educentialApi';

const SkillsDashboard = () => {
  const [enrollments, setEnrollments] = useState([]);
  const [certifications, setCertifications] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      const [enrollmentsRes, certificationsRes] = await Promise.all([
        api.getMyLearning(),
        api.getMyCertifications(),
      ]);
      
      setEnrollments(enrollmentsRes.data || []);
      setCertifications(certificationsRes.data || []);
    } catch (err) {
      console.error('Error fetching dashboard data:', err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
      </div>
    );
  }

  const activeEnrollments = enrollments.filter((e) => e.status === 'ACTIVE');
  const completedEnrollments = enrollments.filter((e) => e.status === 'COMPLETED');

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Skill Development</h1>
              <p className="text-gray-600 mt-1">Learn, grow, and earn certifications</p>
            </div>
            <Link
              to="/services"
              className="px-4 py-2 text-sm text-gray-600 hover:text-gray-900"
            >
              Back to Services
            </Link>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <StatCard label="Active Courses" value={activeEnrollments.length} icon={BookOpen} color="purple" />
          <StatCard label="Completed Courses" value={completedEnrollments.length} icon={CheckCircle} color="green" />
          <StatCard label="Certifications" value={certifications.length} icon={Trophy} color="yellow" />
          <StatCard
            label="Avg. Progress"
            value={`${Math.round(enrollments.reduce((sum, e) => sum + e.progress, 0) / (enrollments.length || 1))}%`}
            icon={BarChart}
            color="blue"
          />
        </div>

        {/* Quick Actions */}
        <div className="mb-8">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Quick Actions</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <ActionCard
              title="Browse Catalog"
              description="Explore available courses"
              icon={Book}
              link="/services/skills/catalog"
              color="purple"
            />
            <ActionCard
              title="My Learning"
              description="Continue your courses"
              icon={Target}
              link="/services/skills/my-learning"
              color="blue"
            />
            <ActionCard
              title="Certifications"
              description="View your achievements"
              icon={Trophy}
              link="/services/skills/certifications"
              color="yellow"
            />
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Active Courses */}
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-semibold text-gray-900">Continue Learning</h2>
              <Link to="/services/skills/my-learning" className="text-purple-600 hover:text-purple-700 text-sm">
                View All
              </Link>
            </div>
            {activeEnrollments.length > 0 ? (
              <div className="space-y-4">
                {activeEnrollments.slice(0, 3).map((enrollment) => (
                  <div key={enrollment.id} className="border border-gray-200 rounded-lg p-4">
                    <h3 className="font-medium text-gray-900 mb-2">Course #{enrollment.courseId}</h3>
                    <div className="w-full bg-gray-200 rounded-full h-2 mb-2">
                      <div
                        className="bg-purple-600 h-2 rounded-full"
                        style={{ width: `${enrollment.progress}%` }}
                      ></div>
                    </div>
                    <div className="flex justify-between items-center">
                      <p className="text-xs text-gray-600">{enrollment.progress}% complete</p>
                      <p className="text-xs text-gray-500">
                        {enrollment.completedLessons.length} lessons completed
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-gray-500">
                <p>No active courses yet</p>
                <Link to="/services/skills/catalog" className="text-purple-600 hover:text-purple-700 mt-2 inline-block">
                  Browse course catalog
                </Link>
              </div>
            )}
          </div>

          {/* Recent Certifications */}
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-semibold text-gray-900">Recent Certifications</h2>
              <Link to="/services/skills/certifications" className="text-purple-600 hover:text-purple-700 text-sm">
                View All
              </Link>
            </div>
            {certifications.length > 0 ? (
              <div className="space-y-3">
                {certifications.slice(0, 3).map((cert) => (
                  <div key={cert.id} className="border-l-4 border-yellow-500 pl-4 py-2">
                    <p className="font-medium text-gray-900">{cert.title}</p>
                    <p className="text-sm text-gray-600">Issued by {cert.issuer}</p>
                    <p className="text-xs text-gray-500 mt-1">
                      {new Date(cert.issuedDate).toLocaleDateString()}
                    </p>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-gray-500">
                <p>No certifications yet</p>
                <p className="text-sm mt-2">Complete courses to earn certificates</p>
              </div>
            )}
          </div>
        </div>

        {/* Learning Paths Preview */}
        <div className="mt-8 bg-white rounded-lg shadow p-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Recommended Learning Paths</h2>
          <p className="text-gray-600 mb-4">
            Structured paths to help you achieve your career goals
          </p>
          <Link
            to="/services/skills/catalog"
            className="inline-block px-6 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
          >
            Explore Learning Paths
          </Link>
        </div>
      </div>
    </div>
  );
};

const StatCard = ({ label, value, icon: Icon, color }) => {
  const colors = {
    purple: 'bg-purple-50 text-purple-700',
    green: 'bg-green-50 text-green-700',
    yellow: 'bg-yellow-50 text-yellow-700',
    blue: 'bg-blue-50 text-blue-700',
  };

  return (
    <div className="bg-white rounded-lg shadow p-6">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-gray-600 text-sm">{label}</p>
          <p className="text-3xl font-bold text-gray-900 mt-2">{value}</p>
        </div>
        <div className={`${colors[color]} p-3 rounded-lg`}><Icon className="w-8 h-8" /></div>
      </div>
    </div>
  );
};

const ActionCard = ({ title, description, icon: Icon, link, color }) => {
  const colors = {
    purple: 'bg-purple-600 hover:bg-purple-700',
    blue: 'bg-blue-600 hover:bg-blue-700',
    yellow: 'bg-yellow-600 hover:bg-yellow-700',
  };

  return (
    <Link to={link} className={`${colors[color]} text-white rounded-lg p-6 transition-colors`}>
      <div className="mb-4"><Icon className="w-10 h-10" /></div>
      <h3 className="font-semibold text-lg mb-1">{title}</h3>
      <p className="text-sm opacity-90">{description}</p>
    </Link>
  );
};

export default SkillsDashboard;
